import { Component, HostListener, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from './services/auth/auth.service';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { HelperService } from './services/helper/helper.service';
import { MaintenanceService } from './services/auth/maintenance.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { Crisp } from 'crisp-sdk-web';
import { ChatbotService } from './services/chatbot/chatbot.service';
import { filter } from 'rxjs/operators';
import { Language } from './constants/supportedLanguages';
import { NgSelectConfig } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { locales } from './constants/supportedLanguages';
import * as en from 'dayjs/locale/en';
import * as en_gb from 'dayjs/locale/en-gb';
import updateLocale from 'dayjs/plugin/updateLocale';
import { LoadingComponent } from '@components/loading/loading.component';
import { DashboardMaintenance } from '@airwallet/shared-models/dashboard-maintenance';
import { SidebarComponent } from './navigation/nav-sidebar/sidebar.component';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { AdminSidebarComponent } from './navigation/admin-sidebar/admin-sidebar.component';
import { TopbarComponent } from './navigation/topbar/topbar.component';
import { SearchBarComponent } from '@components/search/search-bar/search-bar.component';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';
import { FeatureService } from '@services/feature-toggle/feature.service';

dayjs.extend(updateLocale);
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, LoadingComponent, SidebarComponent, AdminSidebarComponent, TopbarComponent, SearchBarComponent]
})
export class AppComponent implements OnInit, OnDestroy {
    maintenanceSub: Subscription;
    isAdmin: Signal<boolean> = this.authService.isAdmin;
    isSignedIn: Signal<boolean> = this.authService.isSignedIn;
    isReady: Signal<boolean> = this.featureService.accessMapReady;
    /**
     * HostListener for window resize events.
     * Adjusts the sidebar visibility based on the current screen width.
     */
    @HostListener('window:resize', ['$event'])
    onResize(): void {
        this.sidebarService.adjustSidebarForScreenWidth(window.innerWidth);
    }
    /**
     * HostListener for document click events.
     * Closes the sidebars if the click occurs on 'main-content-overlay'.
     *
     * @param event - The mouse click event triggered on the document.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        this.sidebarService.closeSidebars(event);
    }

    private routeSubscription: Subscription;
    document = inject(DOCUMENT);
    constructor(
        protected authService: AuthService,
        protected featureService: FeatureService,
        private localStorageService: LocalStorageService,
        private helperService: HelperService,
        private maintenanceService: MaintenanceService,
        private chatbotService: ChatbotService,
        private config: NgSelectConfig,
        private translate: TranslateService,
        protected sidebarService: SidebarService,
        private router: Router,
        private titleService: Title
    ) {
        // below is translations set in the config for ng-select to use in the entire app
        this.config.notFoundText = this.translate.instant('ng_select.not_found');
        this.config.typeToSearchText = this.translate.instant('ng_select.type_to_search');
        this.config.addTagText = this.translate.instant('ng_select.add_item');
        this.config.loadingText = this.translate.instant('ng_select.loading');
        this.config.clearAllText = this.translate.instant('ng_select.clear_all');

        this.maintenanceSub = this.helperService.getMaintenanceState().subscribe(state => {
            const maintenance: DashboardMaintenance = state.snapshot.val();
            this.maintenanceService.maintenance.set(maintenance);
            this.maintenanceService.checkingForMaintenance.set(false);
            const now: number = moment().unix();
            const isUnderMaintenance: boolean = (maintenance.start ?? 0) <= now && (maintenance.end ?? 0) > now;
            const url = window.location.href;

            if (isUnderMaintenance) {
                if (!url.includes('sign-in')) {
                    this.authService.signOut();
                }
            }
        });
    }

    async ngOnInit() {
        const script = this.document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.recaptchaSiteKey}`;
        script.async = true;
        script.defer = true;
        this.document.head.appendChild(script);

        this.sidebarService.adjustSidebarForScreenWidth(window.innerWidth);

        Crisp.configure('6a5a5077-f61b-4a5f-9fd9-4f514ab13ecf', {
            cookieExpire: 3600 * 24,
            locale: this.localStorageService.getItem('language') ? this.localStorageService.getItem('language').language_code : 'en'
        });

        Crisp.setZIndex(999999999999999); // to make sure it's on top of everything
        Crisp.session.setSegments(['dashboard'], true);
        Crisp.session.onLoaded(async () => {
            this.authService.permissionsReady.then(() => {
                this.chatbotService.setupChatBot();
            });
        });

        $crisp.push(['safe', true]);

        if (!window.location.pathname.includes('/sign-in')) {
            this.localStorageService.setItem('callbackUrl', window.location.pathname); // trying to access a guarded page, will remember for redirect after sign in.
        }

        this.setDayjsLocale();
        if (window.location.pathname.includes('/reset-password') || window.location.pathname.includes('/set-password') || (window.location.pathname.includes('/member') && !window.location.pathname.includes('/members'))) {
            // user is signed in and trying to access an unguarded page, our ungarded pages are used for non-authenticated users, so we sign them out;
            // this is used for member/subcustomer's invitation links, and sign-up
            await this.authService.signOut(true);
        }

        this.routeSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
            setTimeout(() => {
                this.updateActiveNavLink();
            }, 0);
        });
    }

    ngOnDestroy() {
        // Unsubscribe to avoid memory leaks
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    updateActiveNavLink() {
        const currentUrl = this.router.url;

        // Update the isFullScreenRoute signal in SidebarService
        this.sidebarService.isFullScreenRoute.set(this.sidebarService.fullScreenRoutes.some(route => currentUrl.includes(route)));

        // Remove 'active' class from all nav links
        const navLinks: NodeListOf<Element> = document.querySelectorAll('.nav-link');
        navLinks.forEach(link => link.classList.remove('active'));

        // Find the last valid ID in the URL that matches an actual nav-link ID
        const urlSegments: string[] = currentUrl.split(/[/?]/); // Splits url by '/' and '?'
        const validIds: string[] = Array.from(navLinks).map(link => link.getAttribute('id'));

        // Reverse the segments to find the last valid ID (I.e. when navigating to /locations/xxx/devices/xxx, we want to find 'nav-devices' last)
        const lastId: string = urlSegments.reverse().find(segment => validIds.includes(`nav-${segment}`));
        const isSearchPage = currentUrl.startsWith('/search');

        if (!lastId && !isSearchPage) {
            return;
        }

        // Add 'active' class to the link matching the last id
        const targetId = `nav-${lastId}`;
        navLinks.forEach(link => {
            const route: string = link.getAttribute('id');
            if (route === targetId) {
                link.classList.add('active');
            }
        });
        // Update the page title
        const activeRouteSnapshot: ActivatedRouteSnapshot = this.router.routerState.snapshot.root.firstChild;
        if (activeRouteSnapshot) {
            const title: string = this.getTitle(activeRouteSnapshot);
            this.titleService.setTitle(title);
        }
    }

    getTitle(next: ActivatedRouteSnapshot): string {
        const path: string = next.routeConfig.path;
        const pathArr: string[] = path.split('/');

        if (!next.queryParams['name'] && !next.queryParams['q'] && !next.queryParams['interval']) {
            if (path.includes(':')) {
                return 'Airwallet';
            }
            return this.translate.instant(`navbar.${pathArr[pathArr.length - 1]}`);
        }

        if (next.queryParams['interval']) {
            return this.translate.instant('navbar.subscription-plan/payment');
        }

        if (next.queryParams['q']) {
            return this.translate.instant('search.common.search_results') + ` - ${next.queryParams['q']}`;
        }

        return `${next.queryParams['name']}`;
    }

    async setDayjsLocale() {
        let browserLang = navigator.language;

        const lang: Language = this.localStorageService.getItem('language');
        if (lang) {
            browserLang = lang.language_code;
        }

        let LOCALE = browserLang.split('-')[0];
        let end = browserLang.split('-')[1];
        if (navigator.language.substring(0, 2) === 'nb' || navigator.language.substring(0, 2) === 'nn') {
            LOCALE = 'no';
            end = undefined;
        }

        const fullString = (end ? LOCALE + '_' + end : LOCALE).toLocaleLowerCase();
        if (locales[fullString] === undefined) {
            if (browserLang === 'en-US') {
                const localeObject = { ...en };

                dayjs.locale('en-us', localeObject);
            }
        } else {
            // Update Dayjs
            dayjs.updateLocale('en', { ...en_gb });
            dayjs.locale(fullString);
        }
    }
}
